<script setup lang="ts">
    import {
        AtmAlgoliaAutocompleteBestsellers,
        AtmAlgoliaAutocompleteCard,
        AtmAlgoliaAutocompletePanelLayout,
        AtmAlgoliaAutocompleteSuggestedLinks,
        AtmChip,
        MolAlgoliaNoResults,
    } from '#components';
    import { h, Fragment, render } from 'vue';
    import { autocomplete, getAlgoliaResults, type VNode } from '@algolia/autocomplete-js';
    import '@algolia/autocomplete-theme-classic';
    import type { AlgoliaProduct } from '@nuxtjs/algolia';
    import type { AlgoliaAutocompleteBestsellersProps } from '~/components/atm/AlgoliaAutocompleteBestsellers.vue';
    import type { Asset } from '~/@types/cms';
    import type { AlgoliaAutocompleteSuggestedLinksProps } from '~/components/atm/AlgoliaAutocompleteSuggestedLinks.vue';
    import type { AlgoliaAutocompletePanelLayoutProps } from '~/components/atm/AlgoliaAutocompletePanelLayout.vue';
    import type { ChipProps } from '~/components/atm/Chip.vue';

    interface AlgoliaAutocompleteProps {
        display?: 'mobile' | 'desktop';
    }

    export interface AlgoliaAutocompleteLink {
        url: string;
        title: string;
        image: string;
    }

    const props = withDefaults(defineProps<AlgoliaAutocompleteProps>(), {
        display: 'mobile',
    });

    const autocompleteElement = useTemplateRef<HTMLElement>('autocompleteElement');

    const { n, m, language } = useLocales();
    const { t } = useI18n({
        useScope: 'local',
    });
    const { algoliaIndex, algoliaSearchClient } = useAlgolia();
    const { setInstantSearchUiState } = useAlgoliaStore();
    const nuxtApp = useNuxtApp();
    const { showBikefinderButton, bikefinderLocaleUrl } = useBikefinder();
    const img = useImage();
    const appStore = useAppStore();
    const { searchConfiguration } = storeToRefs(appStore);

    const sourceId = 'products';

    let autocompleteInstance = ref<ReturnType<typeof autocomplete<AlgoliaProduct>>>();
    let mediaQueryCleanup: ReturnType<typeof subscribeToMediaQuery> | undefined;

    onMounted(async () => {
        const [{ createRedirectUrlPlugin }, { createQuerySuggestionsPlugin }] = await Promise.all([
            import('@algolia/autocomplete-plugin-redirect-url'),
            import('@algolia/autocomplete-plugin-query-suggestions'),
        ]);

        const redirectUrlPlugin = createRedirectUrlPlugin({
            onRedirect(redirects) {
                const item = redirects.find((redirect) => redirect.sourceId === sourceId);
                const url = item?.urls[0];
                if (!url) return;

                navigateTo(url);
            },
        });

        const popularSearchesPlugin = createQuerySuggestionsPlugin({
            searchClient: algoliaSearchClient,
            indexName: `PROD_${language.value.toUpperCase()}_query_suggestions`,
            getSearchParams() {
                return {
                    hitsPerPage: 10,
                };
            },
            // @ts-expect-error due to vue/preact type mismatch
            transformSource({ source }) {
                return {
                    ...source,
                    sourceId: 'querySuggestionsPlugin',
                    getItemInputValue({ item }) {
                        return item.query;
                    },
                    onSelect({ item, setIsOpen }) {
                        autocompleteInstance.value?.setQuery(item.query);
                        setIsOpen(true);
                    },
                    templates: {
                        header() {
                            return h(
                                'div',
                                {
                                    class: 'mb-2 text-lg font-bold md:mb-6',
                                },
                                t('algolia.popularSearchesHeading'),
                            );
                        },
                        item({ item }) {
                            return h(AtmChip, {
                                item: item.query,
                                hideButton: true,
                                tag: 'div',
                            } satisfies ChipProps);
                        },
                    },
                };
            },
        });

        mediaQueryCleanup = subscribeToMediaQuery(
            props.display === 'desktop' ? defaultQueries.min.desktop : defaultQueries.max.tablet,
            async (event) => {
                if (!event.matches) return autocompleteInstance.value?.destroy();
                if (!autocompleteElement.value) return;
                // Clean up old elements
                const oldElements = autocompleteElement.value.querySelectorAll('.aa-Autocomplete:not(.ssr-placeholder)');
                if (oldElements) Array.from(oldElements).forEach((element) => element.remove());
                // wait until other instance is destroyed
                await wait(300);
                autocompleteInstance.value = autocomplete<AlgoliaProduct>({
                    container: autocompleteElement.value,
                    placeholder: t('placeholder'),
                    plugins: [redirectUrlPlugin, popularSearchesPlugin],
                    translations: {},
                    detachedMediaQuery: defaultQueries.max.tablet,
                    openOnFocus: true,
                    panelPlacement: 'full-width',
                    insights: true,
                    getSources({ query }) {
                        return [
                            {
                                sourceId,
                                getItems() {
                                    return getAlgoliaResults({
                                        searchClient: algoliaSearchClient,
                                        queries: [
                                            {
                                                indexName: algoliaIndex.indexName,
                                                params: {
                                                    query,
                                                    // Desktop: 6 columns with 4 rows
                                                    hitsPerPage: 24,
                                                    // triggers rules configured only with this context
                                                    ruleContexts: ['enable-redirect-url'],
                                                },
                                            },
                                        ],
                                    });
                                },
                                templates: {
                                    header({ state, items }) {
                                        if (!state.query || items.length === 0) return '';
                                        // @ts-expect-error due to a difference in type structure of vue and preact
                                        return h(
                                            'div',
                                            {
                                                class: 'mb-6 text-lg font-bold',
                                            },
                                            [t('heading'), ' ', h('span', { class: 'text-woom-red' }, state.query)],
                                        ) as VNode;
                                    },
                                    item({ item }) {
                                        const url = nuxtApp.$helpers.generateLocalePath(item.url.url);
                                        const availableVariant = item?.variants?.items?.find((variant) => variant?.hidden === false);
                                        const imageUrl = availableVariant ? getAssetUrl(`${availableVariant.sku}-side`) : '';
                                        const provider = getAssetProvider(imageUrl);
                                        // @ts-expect-error https://github.com/nuxt/image/issues/2014
                                        const image = img(imageUrl, { width: 360, preset: '3to2' }, { provider });
                                        const price = availableVariant?.price
                                            ? m(Number(availableVariant.price.amount), availableVariant.price.currencyCode)
                                            : '';
                                        const years =
                                            item.min_age && item.max_age ? `${n(item.min_age)} - ${n(item.max_age)} ${t('bikeDetail.years')}` : '';
                                        // @ts-expect-error due to a difference in type structure of vue and preact
                                        return h(AtmAlgoliaAutocompleteCard, {
                                            url,
                                            title: item.title || '',
                                            image,
                                            price,
                                            years,
                                            // as we can't use client side navigation in `AtmAlgoliaAutocompleteCard` we need to handle it here
                                            resolveItemNavigation: () => navigateTo(url),
                                        }) as VNode;
                                    },
                                    noResults() {
                                        // @ts-expect-error due to a difference in type structure of vue and preact
                                        return h(MolAlgoliaNoResults, {
                                            query,
                                            noResultHeading: t('algolia.noResultHeading'),
                                            noResultMessage: t('algolia.noResultMessage'),
                                            bikefinderText: t('bikefinder.label'),
                                            bikefinderUrl: bikefinderLocaleUrl.value,
                                            showBikefinderButton: showBikefinderButton.value,
                                        }) as VNode;
                                    },
                                },
                                getItemUrl({ item }) {
                                    return nuxtApp.$helpers.generateLocalePath(item.url.url);
                                },
                                getItemInputValue({ item }) {
                                    return removeWoomTextFormat(item.title as string);
                                },
                            },
                        ];
                    },
                    navigator: {
                        navigate({ itemUrl }) {
                            navigateTo(itemUrl);
                        },
                    },
                    onSubmit({ state }) {
                        // update vue-instantsearch state on search result page
                        setInstantSearchUiState({ query: state.query });
                        navigateTo({
                            path: nuxtApp.$helpers.generateLocalePath('/products'),
                            query: {
                                query: state.query,
                            },
                        });
                    },
                    onReset() {
                        // update vue-instantsearch state on search result page
                        setInstantSearchUiState({ query: '' });
                    },
                    // @ts-expect-error due to a difference in type structure of vue and preact
                    renderer: { createElement: h, Fragment, render },
                    render({ elements, render, state, setIsOpen }, root) {
                        const { products, querySuggestionsPlugin } = elements;
                        const hasQuery = state.query.length > 0;
                        const hasQuerySuggestions = (querySuggestionsPlugin as any)?.children?.length > 0;

                        const resolveItemNavigation = (url: string) => {
                            setIsOpen(false);
                            navigateTo(url);
                        };

                        const suggestedLinks =
                            !hasQuery && searchConfiguration.value?.suggested_links?.length
                                ? h(AtmAlgoliaAutocompleteSuggestedLinks, {
                                      headline: t('headingSuggestedLinks'),
                                      resolveItemNavigation,
                                      items: searchConfiguration.value.suggested_links.map((item) => {
                                          const url = nuxtApp.$helpers.generateLocalePath(item.url);
                                          const { src, provider } = getAssetInfo({ data: item.image as Asset });
                                          // @ts-expect-error https://github.com/nuxt/image/issues/2014
                                          const image = src ? img(src, { width: 500, preset: '3to2' }, { provider }) : '';
                                          return {
                                              url,
                                              image,
                                              title: item.title,
                                          } satisfies AlgoliaAutocompleteLink;
                                      }),
                                  } satisfies AlgoliaAutocompleteSuggestedLinksProps)
                                : undefined;

                        const bestsellers =
                            !hasQuery && searchConfiguration.value?.bestsellers?.length
                                ? h(AtmAlgoliaAutocompleteBestsellers, {
                                      headline: t('headingBestsellers'),
                                      resolveItemNavigation,
                                      items: searchConfiguration.value.bestsellers.map((item) => {
                                          const url = nuxtApp.$helpers.generateLocalePath(item.url);
                                          const { src, provider } = getAssetInfo({ data: item.image as Asset });
                                          // @ts-expect-error https://github.com/nuxt/image/issues/2014
                                          const image = src ? img(src, { width: 500, preset: '3to2' }, { provider }) : '';
                                          return {
                                              url,
                                              image,
                                              title: item.title,
                                          } satisfies AlgoliaAutocompleteLink;
                                      }),
                                  } satisfies AlgoliaAutocompleteBestsellersProps)
                                : undefined;
                        render(
                            h(AtmAlgoliaAutocompletePanelLayout, { hasQuery, hasQuerySuggestions } satisfies AlgoliaAutocompletePanelLayoutProps, {
                                default: () => [hasQuery ? products : suggestedLinks],
                                bestsellers: () => [bestsellers],
                                suggestions: () => [querySuggestionsPlugin],
                            }),
                            root,
                        );
                    },
                });
            },
        );
    });
    onUnmounted(() => {
        if (mediaQueryCleanup) mediaQueryCleanup();
        autocompleteInstance.value?.destroy();
    });
</script>
<template>
    <div
        class="mol-algolia-autocomplete relative lg:w-96 2xl:ml-[calc(50vw-32px)] 2xl:-translate-x-1/2"
        ref="autocompleteElement">
        <!-- ssr placeholder -->
        <div
            class="aa-Autocomplete ssr-placeholder inset-0 flex items-center justify-end rounded-md border-woom-grey-mid outline-none outline-offset-[-2px] max-lg:h-12 max-lg:w-12 max-lg:opacity-60 lg:absolute lg:mx-0.5 lg:h-[38px] lg:border lg:bg-white lg:pl-2.5"
            role="presentation"
            v-show="!autocompleteInstance">
            <span class="absolute left-0 pl-2.5 text-woom-grey-mid max-lg:hidden">{{ t('placeholder') }}</span>
            <div class="aa-SubmitButton z-xs flex items-center justify-center">
                <svg
                    class="aa-SubmitIcon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor">
                    <path
                        d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path>
                </svg>
            </div>
        </div>
    </div>
</template>
<style>
    /* Base containers for algolia autocomplete */
    .mol-algolia-autocomplete,
    .aa-DetachedOverlay {
        --aa-search-input-height: 36px;
        /* See woom grey */
        --aa-background-color-rgb: 245 245 245;
        /* Form container with children of search input and search button */
        .aa-Form {
            @apply rounded-md border border-woom-grey-mid bg-white pl-2.5 outline-none outline-offset-[-2px] focus-within:border-2 focus-within:shadow-none focus-within:outline-[#9AB1F0];
        }
        .aa-InputWrapper {
            order: 1;
        }
        /* Reset button */
        .aa-InputWrapperSuffix {
            /* TODO: algolia should we show reset button for a11y reasons? */
            @apply hidden;
        }
        /* Search button wrapper */
        .aa-InputWrapperPrefix {
            order: 3;
        }
        /* Search button in input */
        .aa-SubmitButton {
            border-radius: 4px;
            @apply m-0.5 bg-woom-black p-0 hover:bg-woom-black-800 active:bg-woom-black-300;
        }
        /* Search button icon */
        .aa-SubmitIcon {
            @apply mx-auto text-white max-lg:h-6 max-lg:w-6;
        }
        /* Search button in detached mode; screen < lg */
        .aa-DetachedSearchButton {
            @apply h-12 w-12 justify-center border-0 bg-transparent p-0 max-lg:opacity-100 max-md:bottom-[6px] max-md:h-12 max-md:w-12 max-md:rounded-none;
        }
        /* search button with icon in mobile nav - reset focus styles to match woom focus */
        .aa-DetachedSearchButton:focus {
            border-color: revert;
            box-shadow: revert;
            outline: revert;
        }
        .aa-DetachedSearchButton:hover svg {
            @apply max-lg:fill-woom-red max-lg:transition-colors;
        }
        /* Search autocomplete container mobile adaptions */
        .aa-DetachedSearchButtonPlaceholder,
        .aa-DetachedSearchButtonQuery {
            @apply sr-only;
        }
    }
    .aa-DetachedOverlay {
        /* resets floating modal  */
        .aa-DetachedContainer--modal {
            @apply top-0 h-[100dvh] border-none;
        }
        /* Search button in input */
        .aa-SubmitButton {
            @apply h-8 w-10;
        }
    }
    /* styling only for container (not in detached mode) */
    .mol-algolia-autocomplete {
        height: var(--aa-search-input-height);

        /* Search button in input */
        .aa-SubmitButton {
            @apply h-12 w-12 bg-transparent max-md:-translate-y-[6px] max-md:rounded-none lg:h-8 lg:w-10 lg:bg-woom-black;
        }
        .aa-SubmitIcon {
            @apply h-6 text-woom-black lg:text-white;
        }
    }

    /* adaptions for search result panel */
    .aa-DetachedContainer--modal .aa-PanelLayout {
        --aa-detached-modal-max-height: 100%;
    }
    .aa-Panel {
        --aa-panel-shadow: none;
        --aa-panel-max-height: 80vh;
        /* need to make sure drop shadow only shows bottom */
        filter: drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 3px 2px rgb(0 0 0 / 0.06));
        @apply z-md rounded-none rounded-b-lg bg-woom-white-smoke lg:-translate-y-1.5;
    }
    /* adaptions for scrollable search result panel */
    .aa-Panel--scrollable {
        @apply h-full lg:mx-auto lg:min-w-[960px] lg:max-w-[960px] xl:min-w-[1200px] xl:max-w-[1200px];
        scrollbar-color: unset;
        scrollbar-width: unset;
    }
    .aa-SourceNoResults {
        @apply p-0;
    }
    body:has(.aa-Panel) {
        .mol-algolia-autocomplete {
            @apply ml-0 w-full translate-x-0;
        }
        .header-bar-items {
            @apply hidden;
        }
        .header-bar-close-button {
            @apply flex;
        }
    }
    body:not(:has(.aa-Panel)) {
        .header-bar-search {
            @apply mx-0 min-w-0 max-w-none shrink;
        }
    }
    /* Base container for algolia autocomplete < lg */
    @media (width < 1024px) {
        .aa-DetachedOverlay {
            --aa-detached-modal-max-width: 100vw;
            /* less spacing on design than container.vue */
            --aa-spacing-half: 10px;
            @apply bg-woom-grey;
        }
    }
    /* Make space for the close "button" */
    @media (1024px <= width <= 1080px) or (1264px <= width <= 1320px) {
        .header-bar-search {
            @apply pr-12;
        }
    }
</style>
<i18n lang="json">
{
    "da": {
        "placeholder": "Søg efter produkt, kategori eller nøgleord",
        "heading": "Produkter til",
        "headingBestsellers": "Find det, du leder efter – blandt vores mest populære produkter.",
        "headingSuggestedLinks": "Du vil måske også kunne lide"
    },
    "de": {
        "placeholder": "Suchen nach Produkt, Kategorie oder Schlagwort",
        "heading": "Produkte für",
        "headingBestsellers": "Finde, was du suchst – bei unseren beliebtesten Produkten.",
        "headingSuggestedLinks": "Das könnte dir gefallen"
    },
    "en": {
        "placeholder": "Search by product, category or keyword",
        "heading": "Products for",
        "headingBestsellers": "Check out our bestsellers – and find what you're looking for.",
        "headingSuggestedLinks": "You may also like"
    },
    "fr": {
        "placeholder": "Rechercher par produit, catégorie ou mot clé",
        "heading": "Produits pour",
        "headingBestsellers": "Trouvez ce que vous cherchez parmi nos produits les plus populaires.",
        "headingSuggestedLinks": "Vous pourriez aimer"
    },
    "nl": {
        "placeholder": "Op product, categorie of trefwoord zoeken",
        "heading": "Producten voor",
        "headingBestsellers": "Vind wat je zoekt – tussen onze populairste producten.",
        "headingSuggestedLinks": "Dit is misschien ook iets voor je"
    },
    "sv": {
        "placeholder": "Sök efter produkt, kategori eller uppslagsord",
        "heading": "Produkter för",
        "headingBestsellers": "Hitta det du letar efter – bland våra populäraste produkter.",
        "headingSuggestedLinks": "Du kanske också gillar"
    }
}
</i18n>
